import { Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Observable, map, of, take } from 'rxjs';
import { CollectionField, CollectionMetadata, PermissionConditionForm, PermissionConditionRule } from '@newroom-connect/library/interfaces';

import { EntityService } from '../entity/entity.service';

interface CollectionsMetadata {
  collections: CollectionMetadata[];
}

export interface IFlattenedProperty {
  /** The property name (e.g., 'ID', 'name'). */
  name: string;
  /** Full path to the property (e.g., ['Area', 'market', 'name']). */
  path: string[];
  /** Display name for the dropdown (e.g., 'Area → Market → Name'). */
  displayName: string;
  /** Flattened property name for form usage (e.g., 'Area.market.name'). */
  flattenedName: string;
  /** The associated field metadata. */
  field: CollectionField;
  /** Whether this property has nested children. */
  hasChildren?: boolean;
  /** Nested child properties for hierarchical display. */
  children?: IFlattenedProperty[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionConditionService extends EntityService {
  protected readonly searchableProperties: string[] = [];

  private readonly collectionsMetadata = signal<CollectionsMetadata | null>(null);
  public readonly isLoadingMetadata = signal<boolean>(false);
  private readonly flattenedPropertiesCache = new Map<string, IFlattenedProperty[]>();

  constructor() {
    super();
    this.loadMetadata();
  }

  /**
   * Gets the collection metadata including fields and their supported rules.
   *
   * @param collectionName The name of the collection to get metadata for.
   *
   * @returns An observable of the collection metadata.
   */
  public getCollectionMetadata(collectionName: string): Observable<CollectionMetadata | null> {
    const metadata = this.collectionsMetadata();

    // If we have cached metadata, use it
    if (metadata) {
      return of(metadata.collections.find(c => c.name === collectionName) ?? null);
    }

    // If metadata is not loaded yet, load it first
    if (!this.isLoadingMetadata()) {
      this.loadMetadata();
    }

    // Return from API only if we don't have cached data
    return this.apiService.get<CollectionMetadata[] | CollectionsMetadata>('presets/access/collections').pipe(
      take(1),
      map(response => {
        // Handle both response formats: direct array or wrapped object
        let collectionsArray: CollectionMetadata[];

        if (Array.isArray(response)) {
          // Direct array format
          collectionsArray = response;
        } else {
          // Wrapped object format
          collectionsArray = response.collections;
        }

        // Cache the metadata in the expected format
        const metadata: CollectionsMetadata = { collections: collectionsArray };

        this.collectionsMetadata.set(metadata);

        return collectionsArray.find(c => c.name === collectionName) ?? null;
      })
    );
  }

  /**
   * Creates a new permission condition form group.
   *
   * @returns A new permission condition form group.
   */
  public createConditionForm(): FormGroup<PermissionConditionForm> {
    const metadata = this.collectionsMetadata();
    const defaultField = metadata?.collections[0]?.fields[0];

    return this.createFormGroup(defaultField);
  }

  /**
   * Creates a form group for a specific field.
   * @param field
   *
   * @returns A new form group for the field.
   */
  public createFormGroup(field?: CollectionField): FormGroup<PermissionConditionForm> {
    const defaultValues = {
      propertyName: field?.name ?? '',
      propertyValue: this.getDefaultValueForType(field?.type),
      rule: this.getDefaultRuleForType(field?.type),
      nestedConditions: []
    };

    return new FormGroup<PermissionConditionForm>({
      propertyName: new FormControl(defaultValues.propertyName, { nonNullable: true }),
      propertyValue: new FormControl(defaultValues.propertyValue?.toString() ?? '', { nonNullable: true }),
      rule: new FormControl(defaultValues.rule, { nonNullable: true }),
      nestedConditions: new FormArray<FormGroup<PermissionConditionForm>>([])
    });
  }

  /**
   * Updates a form group's values based on a field change.
   * @param form
   * @param field
   */
  public updateFormForField(form: FormGroup<PermissionConditionForm>, field?: CollectionField): void {
    const defaultValue = this.getDefaultValueForType(field?.type);
    const defaultRule = this.getDefaultRuleForType(field?.type);

    form.patchValue({
      propertyValue: defaultValue?.toString() ?? '',
      rule: defaultRule
    }, { emitEvent: false });

    // Clear nested conditions if not a relation field
    if (!field?.relationName && form.controls.nestedConditions && form.controls.nestedConditions.length > 0) {
      form.controls.nestedConditions.clear();
    }
  }

  /**
   * Gets the supported rules for a field based on its type.
   * @param field The collection field to get supported rules for.
   * @returns An array of supported PermissionConditionRules.
   */
  public getSupportedRules(field: CollectionField): PermissionConditionRule[] {
    if (!field) {
      return [];
    }

    switch (field.type.toLowerCase()) {
      case 'string':
        return [
          PermissionConditionRule.EQUALS,
          PermissionConditionRule.CONTAINS,
          PermissionConditionRule.STARTS_WITH,
          PermissionConditionRule.ENDS_WITH
        ];
      case 'number':
      case 'datetime':
        return [
          PermissionConditionRule.EQUALS,
          PermissionConditionRule.GT,
          PermissionConditionRule.LT,
          PermissionConditionRule.GTE,
          PermissionConditionRule.LTE
        ];
      case 'boolean':
        return [PermissionConditionRule.EQUALS];
      default:
        return [];
    }
  }

  /**
   * Gets the default rule for a field type.
   * @param type
   *
   * @returns The default rule for the field type.
   */
  private getDefaultRuleForType(type?: string): PermissionConditionRule {
    if (!type) {
      return PermissionConditionRule.EQUALS;
    }

    switch (type.toLowerCase()) {
      case 'datetime':
      case 'number':
        return PermissionConditionRule.GTE;
      default:
        return PermissionConditionRule.EQUALS;
    }
  }

  /**
   * Gets the default value for a field type.
   * @param type
   *
   * @returns The default value for the field type.
   */
  private getDefaultValueForType(type?: string): Date | boolean | number | string {
    if (!type) {
      return '';
    }

    switch (type.toLowerCase()) {
      case 'datetime':
        return new Date();
      case 'boolean':
        return false;
      case 'number':
        return 0;
      default:
        return '';
    }
  }

  /**
   * Initializes the metadata cache if empty.
   *
   */
  private loadMetadata(): void {
    if (this.isLoadingMetadata() || this.collectionsMetadata()) {
      return;
    }

    this.isLoadingMetadata.set(true);

    this.apiService.get<CollectionMetadata[] | CollectionsMetadata>('presets/access/collections')
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          // Handle both response formats: direct array or wrapped object
          let metadata: CollectionsMetadata;

          if (Array.isArray(response)) {
            // Direct array format
            metadata = { collections: response };
          } else {
            // Wrapped object format
            metadata = response;
          }

          this.collectionsMetadata.set(metadata);
          // Pre-build flattened properties for all collections
          this.buildAllFlattenedProperties(metadata);
          this.isLoadingMetadata.set(false);
        },
        error: () => {
          this.isLoadingMetadata.set(false);
          this.collectionsMetadata.set(null);
          this.flattenedPropertiesCache.clear();
        }
      });
  }

  /**
   * Gets flattened properties for a collection (including nested properties from relations).
   * @param collectionName The name of the collection to get properties for.
   * @returns Array of flattened property options.
   */
  public getFlattenedProperties(collectionName: string): IFlattenedProperty[] {
    return this.flattenedPropertiesCache.get(collectionName) ?? [];
  }

  /**
   * Gets hierarchical properties for a collection (for cascading menu display).
   * @param collectionName The name of the collection to get properties for.
   * @returns Array of hierarchical property options.
   */
  public getHierarchicalProperties(collectionName: string): IFlattenedProperty[] {
    const flattenedProperties = this.getFlattenedProperties(collectionName);

    return this.buildHierarchicalStructure(flattenedProperties);
  }

  /**
   * Pre-builds flattened properties for all collections when metadata is loaded.
   * @param metadata The collections metadata.
   */

  private buildAllFlattenedProperties(metadata: CollectionsMetadata): void {
    this.flattenedPropertiesCache.clear();

    // Build properties for each collection (including nested ones up to 3 levels)
    for (const collection of metadata.collections) {
      const flattenedProperties: IFlattenedProperty[] = [];

      this.addPropertiesRecursively(
        collection,
        metadata.collections,
        [],
        flattenedProperties,
        new Set(),
        3 // Max depth
      );

      this.flattenedPropertiesCache.set(collection.name, flattenedProperties);
    }
  }

  /**
   * Recursively adds properties from a collection and its relations.
   * @param collection The current collection to process.
   * @param allCollections All available collections metadata.
   * @param currentPath Current path being built.
   * @param flattenedProperties Array to add properties to.
   * @param visited Set of visited collection names to prevent cycles.
   * @param maxDepth Maximum nesting depth.
   */
  private addPropertiesRecursively(
    collection: CollectionMetadata,
    allCollections: CollectionMetadata[],
    currentPath: string[],
    flattenedProperties: IFlattenedProperty[],
    visited: Set<string>,
    maxDepth: number
  ): void {
    if (currentPath.length >= maxDepth || visited.has(collection.name)) {
      return;
    }

    const newVisited = new Set(visited);

    newVisited.add(collection.name);

    // Add all fields from current collection
    for (const field of collection.fields) {
      const fieldPath = [...currentPath, field.name];
      const displayName = fieldPath.join(' → ');
      const flattenedName = fieldPath.join('.');

      flattenedProperties.push({
        name: field.name,
        path: fieldPath,
        displayName,
        flattenedName,
        field,
        hasChildren: field.kind === 'object' && !!field.type && currentPath.length < maxDepth - 1
      });

      // If this field has a relation and we haven't reached max depth, recurse
      if (field.kind === 'object' && field.type && currentPath.length < maxDepth - 1) {
        // Find related collection by matching field.type (case-insensitive)
        const relatedCollection = allCollections.find(c =>
          c.name.toLowerCase() === field.type.toLowerCase()
        );

        if (relatedCollection && !newVisited.has(relatedCollection.name)) {
          this.addPropertiesRecursively(
            relatedCollection,
            allCollections,
            fieldPath,
            flattenedProperties,
            newVisited,
            maxDepth
          );
        }
      }
    }
  }

  /**
   * Builds hierarchical structure from flattened properties for cascading menu display.
   * @param flattenedProperties Array of flattened properties.
   * @returns Array of hierarchical properties.
   */
  private buildHierarchicalStructure(flattenedProperties: IFlattenedProperty[]): IFlattenedProperty[] {
    const hierarchical: IFlattenedProperty[] = [];
    const rootProperties = flattenedProperties.filter(p => p.path.length === 1);

    for (const rootProperty of rootProperties) {
      const hierarchicalProperty: IFlattenedProperty = {
        ...rootProperty,
        children: []
      };

      if (rootProperty.hasChildren) {
        // Find all nested properties for this root property
        const nestedProperties = flattenedProperties.filter(p =>
          p.path.length > 1 && p.path[0] === rootProperty.name
        );

        // Group by second level (direct children)
        const directChildren = nestedProperties.filter(p => p.path.length === 2);

        hierarchicalProperty.children = directChildren.map(child => ({
          ...child,
          hasChildren: false // We limit hierarchical display to 2 levels for UI simplicity
        }));
      }

      hierarchical.push(hierarchicalProperty);
    }

    return hierarchical;
  }
}
